import moment from 'moment';
import { getPercentages } from '../../Colors/percentages';
import {trainingAPI} from '../../api/TrainingAPI';
import { 
  getRestTravelLabel,
  getBarColorsForHighcharts,
  getTestingColorFromZScore,
  getBarWeightMetric,
  isVBTMetric,
  getValueColor,
  getRestAndTravelDaysInChart,
  addPredictionSeriesConfig
} from '../../Utils/chart_utils';
import { get_unit, get_dist_unit } from "../../Utils/unit.js";
import { chartExportableOptions } from '../mapperUtil.js'
import { color } from 'highcharts';
export const mapDataForHighcharts = ({
  dataIn,
  metric,
  showACWR = false,
  teamName,
  showPredictions = false,
  teamColors = [],
  isTeamChart = false,
  showAvgPlotline = false,
  singleAxis = false,
  isForcePlateMetric = false,
  isTestingMetric = false,
  isSubjectiveMetric = false,
  isDrillData = false,
  isRPEChart = false,
  games = [],
  showRest = false,
  showTravel = false,
  restAndTravel = null,
  startDate = null,
  endDate = null,
  session = [], 
  playerGroup = [],
  position = [],
  stat = null,
  teamMetric = null,
  ACWRLowerLimit = 0.8,
  ACWRUpperLimit = 1.3,
  showTitle = true,
  enableDrillDown = false,
  showHamburger = true,
  dataColorUtil = undefined,
  selectedPlayerId = null
}) => {
  const acwr_upper_limit = ACWRUpperLimit;
  const acwr_lower_limit = ACWRLowerLimit;
  const metricKey = metric?.key;

  if (!metricKey) {
    console.error(`Metric ${metricKey} not found`);
    return null;
  }

  let tooltip = {
    shared: true
  };
  tooltip = getCustomTooltip(isForcePlateMetric, isSubjectiveMetric, isRPEChart, isTestingMetric, tooltip)

  let data = dataIn?.data || [];
  const colors_100 = dataIn?.colors;
  const categories = [];
  const series = [];
  const acwrSeries = [];
  const perMinuteSeries = [];
  const predictionSeries = [];
  const secondarySeriesData = [];

  const acwrDataPointKey = metricKey + "_ACWR";
  const perMinuteDataPointKey = metricKey + "_per_minute";
  const predictionDataPointKey = metricKey + "_pre";
  const percentages = getPercentages(metricKey);

  const max_value = Math.max(
    ...(data.map((dp) => isForcePlateMetric ? dp[metricKey + "__max"] : dp[metricKey]).filter((x) => x) || [])
  );
  const min_value = Math.min(
    ...(data.map((dp) => isForcePlateMetric ? dp[metricKey + "__min"] : dp[metricKey]).filter((x) => x) || [])
  );

  const best_value = metric?.low_good ? min_value : max_value;			
  const isVBT = isVBTMetric(metric?.name);
  const barWeightMetric = data.length && isTestingMetric ? getBarWeightMetric(metric?.name, Object.keys(data[0])) : null

  if(showRest || showTravel) {
    const restAndTravelDates = getRestAndTravelDaysInChart(
      startDate,
      endDate,
      data,
      restAndTravel,
      showRest,
      showTravel
    );

    const maxPreValue = Math.max(
      ...(data.map((dp) => dp[metricKey + "__pre"]).filter((x) => x) || [])
    );
  
    data = data.concat(
      restAndTravelDates.map((dateData) => ({
        ...dateData,
        disp_date: moment(dateData.date).format("MM-DD"),
        [metricKey]: showPredictions
          ? showPredictions
            ? Math.max(max_value, maxPreValue)
            : max_value
          : max_value,
        isRestOrTravel: true,
      }))
    );
  }

  data.sort((a, b) => {
    if(isTeamChart && !isDrillData) {
		  return a[metricKey] > b[metricKey] ? -1 : 1;
    } else {
      return a.date < b.date ? -1 : 1;
    }
	});

  data.forEach((item, index) => {
    if(isTeamChart) {
      categories.push(item.name);
    } else {
      const opponent = games?.find((x) => x.date === item.date)?.opponent || ''
      categories.push(opponent || moment(item.date).format('MM-DD'));
    }

    const { color, is_max } = getTestingColorFromZScore(
      item,
      metric?.name,
      best_value,
      metric?.low_good
    );

    const isRestOrTravel = item.isRestOrTravel || false;

    if (isForcePlateMetric) {
      const max = item[`${metric.name}__max`];
      const min = item[`${metric.name}__min`];
      const diff = item[`${metric.name}__diff`];
      const avg = item[`${metric.name}__avg`];
      const sum = item[`${metric.name}__sum`];

      let y = avg
      if (typeof(stat) === 'string' && stat.toLowerCase() === 'max') {
        y = ((metric.low_good ?? false) ? min : max)
      } else if (typeof(stat) === 'string' && stat.toLowerCase() === 'sum') {
        y = sum
      }

      series.push({ y, color, is_max, max, min, diff, ...(enableDrillDown && {drilldown: `drilldown-${index}`})});
      if (!(typeof(stat) === 'string' && stat.toLowerCase() === 'sum')) {
        secondarySeriesData.push([min, max]);
      }
    } else if (isTestingMetric) {
      let value = item[metric.name];
      const max = item[`${metric.name}__max`];
      const min = item[`${metric.name}__min`];
      const sum = item[`${metric.name}__sum`];

      if (typeof(stat) === 'string' && stat.toLowerCase() === 'sum') {
        value = sum
      } else if (typeof(stat) === 'string' && stat.toLowerCase() === 'max') {
        value = max
      }
      
      series.push({y: value, max, min, color, ...(enableDrillDown && {drilldown: `drilldown-${index}`})})

      if(isVBT && barWeightMetric) {
        const barWeightValue = item[barWeightMetric];
        secondarySeriesData.push(barWeightValue)
      }
    } else if (isSubjectiveMetric) {
      const value = item[metricKey] !== undefined ? item[metricKey] : null;
      if(isRPEChart) {
        const barColor = getValueColor(colors_100, { value: item?.RPE }, "RPE") 
        series.push({
          y: value,
          color: barColor,
          short: "Session RPE",
          ...(enableDrillDown && {drilldown: `drilldown-${index}`})
        });

        const wellnessScoreValue = item["wellness_score"] !== undefined ? item["wellness_score"] : null;
        secondarySeriesData.push(wellnessScoreValue)
      } else {
        const barColor = getBarColorsForHighcharts(item, metricKey, true, metric, dataColorUtil)
        series.push({
          y: value,
          color: barColor,
          text: item?.answerTxt || "",
          short: metric?.short || "",
          ...(enableDrillDown && {drilldown: `drilldown-${index}`})
        });
      }
    } else {
      const value = item[metricKey] !== undefined ? item[metricKey] : null;
      const acwrValue = item[acwrDataPointKey];
      const perMinuteValue = item[perMinuteDataPointKey];
      const predictionValue = item[predictionDataPointKey];
      const barColor = isDrillData ? getBarColorForDrillData(item, metricKey, percentages, colors_100, teamName, dataColorUtil) : getBarColorsForHighcharts(item, metricKey, false, null, dataColorUtil, item.player_id)
      const restTravelLabel = getRestTravelLabel(item)
      series.push({
        y: value,
        color: barColor,
        restTravelLabel,
        isRestOrTravel,
        ...(enableDrillDown && {drilldown: `drilldown-${index}`}),
        ...(restTravelLabel
        ? {
            dataLabels: {
              format: '{point.restTravelLabel}',
              rotation: 270,
              color: 'black',
              style: {
                textOutline: 'none'
              }
            },
            opacity: 0.5,
          } : {})
      });
      acwrSeries.push(acwrValue !== undefined ? acwrValue : null);
      perMinuteSeries.push(perMinuteValue !== undefined ? perMinuteValue : null);
      predictionSeries.push(predictionValue !== undefined ? predictionValue : null);
    }
  });

  const maxValue = Math.max(
    ...(showPredictions ? predictionSeries.filter(v => v !== null) : []),
    ...(isForcePlateMetric ? secondarySeriesData.filter(v => v[1] !== null).map( (v) => v[1]) : []),
    ...series.filter(v => v.y !== null).map( (v) => v.y)
  );

  let has_units = metric.units;
  let unit = "";
  if (has_units) {
    unit = get_unit(metricKey, metric, teamName, teamMetric);
    if (metricKey == "distance") {
      unit = get_dist_unit(teamName, teamMetric);
    }
  }
  const yAxisTitle = (has_units ? unit : "")
  const maximumSeriesValue = Math.ceil(maxValue * 1.1);

  const mainSeries = {
    name: metric?.name || metric?.short || '',
    type: 'column',
    yAxis: 0,
    data: series,
    colorByPoint: false,
    dataLabels: {
      style: {
        textOutline: 'none'
      }
    }
  }

  if(isSubjectiveMetric) {
    mainSeries.dataLabels = {
      ...mainSeries.dataLabels,
      align: 'center',
      rotation: series.length > 4 ? 270 : 0,
      ...(isRPEChart ? {} : {format: '{point.text}'})
    }
  }

  let secondarySeries = {
    dataLabels: {
      style: {
        textOutline: 'none'
      }
    }
  };
  let secondaryMax = 0;
  if(isTestingMetric){
    const max = Math.max(
      ...secondarySeriesData.filter(v => v !== null)
    );
    secondaryMax = Math.ceil(max * 1.1);
    secondarySeries = {
      ...secondarySeries,
      name: barWeightMetric,
      type: 'spline',
      data: secondarySeriesData,
      color: 'rgb(70, 170, 197)',
      yAxis: 1,
      showInLegend: secondarySeriesData.length ? true : false,
    }
  } else if (isForcePlateMetric) {
    secondarySeries = {
      ...secondarySeries,
      name: 'Error',
      type: 'errorbar',
      data: secondarySeriesData,
      yAxis: 0,
    }
  } else if (isRPEChart) {
    const max = Math.max(
      ...secondarySeriesData.filter(v => v !== null)
    );
    secondaryMax = Math.ceil(max * 1.1);
    secondarySeries = {
      ...secondarySeries,
      name: "Wellness Score",
      type: 'spline',
      data: secondarySeriesData,
      color: 'rgb(70, 170, 197)',
      yAxis: 1,
      showInLegend: secondarySeriesData.length ? true : false
    }
  } else {
    const maxAcwrValue = Math.max(
      ...acwrSeries.filter(v => v !== null)
    );
    const maxPerMinuteValue = Math.max(
      ...perMinuteSeries.filter(v => v !== null)
    );
    const maxAcwrSeriesValue = Math.ceil(maxAcwrValue * 1.1);
    const maxPerMinuteSeriesValue = Math.ceil(maxPerMinuteValue * 1.1);
    secondaryMax = showACWR ? maxPerMinuteSeriesValue : maxAcwrSeriesValue
    secondarySeries = {
      ...secondarySeries,
      name: showACWR ? `${metric.name} per minute` : 'ACWR',
      type: 'spline',
      yAxis: 1,
      data: showACWR ? perMinuteSeries : acwrSeries,
      color: 'rgb(70, 170, 197)',
      marker: {
        enabled: true,
        radius: 3
      },
      selected: true,
      showInLegend: singleAxis || (!showACWR && !metric.ACWR) ? false : true,
      connectNulls: true
    }
  }

  mainSeries.data = mainSeries.data.map((item, index) => ({
    ...item,
    ...(enableDrillDown && {drilldown: categories[index]}),
    name: categories[index]
  }));
  const originalSeriesData = [];
  let originalACWRSeries = null;
  let originalXAxisConfig = null;
  let positionsArray = []
  let positionalAvgPlot = [];
  let teamAvgPlot = [];
  let acwrPlot = [];
  let categoriesArray = []
  let playerColors = {}

  let options = {
    chart: {
      type: 'column',
      zoomType: 'xy',
      events: {
        load: function() {
          // Store the original series data, ACWR series, and x-axis configuration
          this.isDrilling = false;
          this.series.forEach(s => {
            if (s.name === 'ACWR') {
              originalACWRSeries = s.options;
            } else {
              originalSeriesData.push(s.options);
            }
          });
          this.options.custom = this.options.custom || {};
          this.options.custom.title = (showTitle ? (metric?.name || metric?.short || '') : '');
          originalXAxisConfig = JSON.parse(JSON.stringify(this.xAxis[0].options));
        },
        drilldown: function(e) {
          if (this.isDrilling) return;
          this.isDrilling = true;
          this.options.custom = this.options.custom || {};
          if (!e.seriesOptions) {
            const chart = this;
            const date = data[e.point.index]?.date;
    
            if (!chart.drilldownLevel) {
              chart.drilldownLevel = 0;
            }

            if (chart.drilldownLevel >= 2) {
              return; 
            }

            if (!chart.drilldownData && chart.drilldownLevel === 0) {
              chart.showLoading('Loading data ...');
              categoriesArray = [...categories]; 

              trainingAPI.getDrillDownChartData({ 
                date: date,
                position,
                player_group: playerGroup,
                sessions: session,
                player_id: selectedPlayerId || null
              })
                .then(response => {
                  const data = response.player_data;
                  playerColors = response.colors;

                  chart.drilldownData = data;

                  const {drilldowns, positions, posAvg} = createDrilldownStructure(data, metric, e.point.name, playerColors, enableDrillDown);

                  chart.yAxis[1].removePlotLine('acwr_upper');
                  chart.yAxis[1].removePlotLine('acwr_lower');
                  if (showAvgPlotline) {
                    chart.yAxis[0].removePlotLine('avg');
                    const avg = posAvg ? posAvg.toFixed(1) : 0;
                    const plotline = getChartTeamAvgPlotLine({avg});
                    positionalAvgPlot = [plotline];
                
                    chart.yAxis[0].update({
                      plotLines: [...positionalAvgPlot]
                    });
                  }
                 
                  positionsArray = [...positions]

                  const xAxis = chart.xAxis[0];
                  xAxis.categories = positions
                  this.options.custom.currentCategories = positions;
                  this.options.custom.drilldown = "positional";
                  const series = drilldowns[e.point.name];  

                  chart.hideLoading();
                  chart.addSeriesAsDrilldown(e.point, series);
                  // addACWRSeries(chart, series.data, 'Positions');
                  
                  chart.applyDrilldown();
                  updateYAxisMax(chart, series.data);
    
                  chart.applyDrilldown();
                  chart.yAxis[1].update({
                    visible: true
                  });
                  chart.drilldownLevel++;
                })
                .catch(error => {
                  console.error('Error fetching drill-down data:', error);
                  chart.hideLoading();
                })
                .finally(() => {
                  this.isDrilling = false;
                });
            } else {
              const {drilldowns, names} = createDrilldownStructure(chart.drilldownData, metric, e.point.name, playerColors, enableDrillDown);
              if (showAvgPlotline && teamAvgPlot.length === 0) {
                teamAvgPlot = [getChartTeamAvgPlotLine({series})];
              }

              if (acwrPlot.length === 0) {
                acwrPlot = getACWRPlotLines();
              }

              const series = drilldowns[e.point.name];
              let playerNames = series?.data?.map(item => item.name);
              let playerAvg = series?.data?.map(item => item.y).reduce((sum, y) => sum + y, 0);

              chart.yAxis[1].removePlotLine('acwr_upper');
              chart.yAxis[1].removePlotLine('acwr_lower');
              if (showAvgPlotline) {
                chart.yAxis[0].removePlotLine('avg');
                const avg = playerAvg ? (playerAvg / (playerNames.length > 0 ? playerNames.length : 0)).toFixed(1) : 0;
            
                const plotline = getChartTeamAvgPlotLine({avg});
            
                chart.yAxis[0].update({
                  plotLines: [plotline]
                });
              }
              
              const xAxis = chart.xAxis[0];
              xAxis.categories = series?.data?.map(item => item.name);
              this.options.custom.currentCategories = xAxis.categories;
              this.options.custom.drilldown = "player";
              chart.addSeriesAsDrilldown(e.point, series);
              // addACWRSeries(chart, series.data, 'Players');
              updateYAxisMax(chart, series.data);
              
              chart.redraw();

              this.isDrilling = false;
              chart.drilldownLevel++;
            }
          }
        },
        drillup: function() {
          console.log('isDrilling drillup: ', this.isDrilling)
          if (this.isDrilling) return;
          this.isDrilling = true;

          const chart = this;
          this.options.custom = this.options.custom || {};
          chart.drilldownLevel--;
          
          try{
            if (chart.drilldownLevel === 1) {
              const xAxis = chart.xAxis[0];

              xAxis.categories = positionsArray;
              this.options.custom.currentCategories = positionsArray;
              this.options.custom.drilldown = "positional";
              chart.yAxis[1].removePlotLine('acwr_upper');
              chart.yAxis[1].removePlotLine('acwr_lower');
              if (showAvgPlotline) {
                chart.yAxis[0].removePlotLine('avg');
                chart.yAxis[0].update({
                  plotLines: positionalAvgPlot
                });
              }

              // let acwrSeriesIndex = -1
              // chart.series.forEach((serie, index) => {
              //   if (serie.name === 'Players ACWR') {
              //     acwrSeriesIndex = index;
              //   }
              // });
              // if (acwrSeriesIndex !== -1) {
              //   chart.series[acwrSeriesIndex].remove(false);
              // } else {
              //   console.log('"Players" series not found');
              // }

              // let positionsACWRIndex = -1
              // chart.series.forEach((serie, index) => {
              //   if (serie.name === 'Positions ACWR'){
              //     positionsACWRIndex = index;
              //   }
              // });
              
              // if (positionsACWRIndex !== -1) {
              //   updateYAxisMax(chart, chart.series[positionsACWRIndex]);
              // } else {
              //   console.log('"Positions ACWR" series not found');
              // }

              chart.redraw();
            }

            if (chart.drilldownLevel === 0) {
              if (chart?.drilldownData) delete chart?.drilldownData;
              const xAxis = chart.xAxis[0];

              xAxis.categories = categories
              this.options.custom.currentCategories = categories;
              this.options.custom.drilldown = "";
              if (showAvgPlotline) {
                chart.yAxis[0].removePlotLine('avg');
                chart.yAxis[0].update({
                  plotLines: teamAvgPlot
                });
              }
              
              chart.yAxis[1].removePlotLine('acwr_upper');
              chart.yAxis[1].removePlotLine('acwr_lower');
              chart.yAxis[1].update({
                plotLines: acwrPlot
              });

              let positionsACWRSeriesIndex = -1
              
              chart.series.forEach((serie, index) => {
                if (serie.name === 'Positions ACWR') {
                  positionsACWRSeriesIndex = index;
                }
              });
              
              if (positionsACWRSeriesIndex !== -1) {
                chart.series[positionsACWRSeriesIndex].remove(false);
              } else {
                console.log('"Positions ACWR" series not found');
              }

              let acwrSeriesIndex = -1
              
              chart.series.forEach((serie, index) => {
                if (serie.name === 'ACWR') {
                  acwrSeriesIndex = index;
                }
              });
              
              if (acwrSeriesIndex !== -1) {
                updateYAxisMax(chart, chart.series[acwrSeriesIndex]);
              } else {
                console.log('"Positions ACWR" series not found');
              }
              chart.redraw();
            }
          } finally {
              this.isDrilling = false;
          }
        }
      }
    },
    drilldown: {
      activeAxisLabelStyle: {
        cursor: 'pointer',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
        fontWeight: 500,
        tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
        tickWidth: 1
      },
      activeDataLabelStyle: {
        cursor: 'pointer',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
      },
      series: []
    },
    title: {
      text: (showTitle ? (metric?.name || metric?.short || '') : '')
    },
    xAxis: {
      categories,
      gridLineWidth: 2,
      lineColor: '#ccd6eb',  
      lineWidth: 1, 
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      tickWidth: 1
    },
    yAxis: [
      {
        title: {
          text: yAxisTitle
        },
        min: maximumSeriesValue < 0 ? maximumSeriesValue : 0,
        max: maximumSeriesValue < 0 ? 0 : maximumSeriesValue,
        tickAmount: 6,
        plotLines: [],
      }
    ],
    legend: {
      enabled: true,
      verticalAlign: 'top',
      layout: 'horizontal'
    },
    tooltip: {
      ...tooltip,
      formatter: function() {
        if (this.point.isRestOrTravel) {
          return false;
        }
        let s = '<b>' + ((Number.isInteger(this.x) && categories[this.x] !== undefined) ? categories[this.x] : this.x) + '</b>';
        this.points.forEach(function(point) {
          if (point?.point?.max && point?.point?.min) {
            s += `<br/>${point?.series?.name} (${(stat[0]?.toUpperCase() + stat.slice(1)) || 'Avg'}): ` + point?.y;
            s += `<br/>${point?.series?.name} (Max): ` + point?.point?.max ;
            s += `<br/>${point?.series?.name} (Min): ` + point?.point?.min ;
          }

          else if(point?.series?.name === 'Error') {
            s += '<br> Max: ' + point?.point?.high ;
            s += '<br> Min: ' + point?.point?.low ;
          } else {
            s += '<br/>' + point.series.name + ': ' + point.y;
          }
        });
        return s;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          rotation: categories.length > 20 ? 270 : 0,
          y: categories.length > 20 ? 10 : 0,
          inside: true
        }
      }
    },
    colors: teamColors,
    series: [mainSeries, secondarySeries],
    exporting: {
      enabled: showHamburger,
      ...chartExportableOptions
    }
  };

  function addACWRSeries(chart, data, type) {
    
    const acwrSeries = {
      name: `${type} ACWR`,
      type: 'spline',
      yAxis: 1,
      data: data.map(item => ({
        name: item.name,
        y: item.acwr
      })),
      color: 'rgb(70, 170, 197)',
      selected: false,
      connectNulls: true,
    };
    
    chart.addSeries(acwrSeries, false);
  }

  function createDrilldownStructure(data, metric, date, colors = null, enableDrillDown = false) {
    const drilldowns = {};
    const positions = [...new Set(data.map(item => item.player_id__position))];
    const acwrKey = `${metric.key}_ACWR`;
    let posSum = 0;

    // Second layer: Positions
    drilldowns[date] = {
      name: 'Positions',
      id: date,
      data: positions.map((position, index) => {
        const positionData = data.filter(item => item.player_id__position === position);
        const avgValue = positionData.reduce((sum, item) => sum + item[metric.key], 0) / positionData.length;
        const avgAcwr = positionData.reduce((sum, item) => sum + (item[acwrKey] || 0), 0) / positionData.length;

        posSum += Number(avgValue?.toFixed(2));

        return {
          name: position,
          y: Number(avgValue?.toFixed(2)),
          acwr: Number(avgAcwr?.toFixed(2)),
          ...(enableDrillDown && {drilldown: position}),
          color: getBarColorsForHighcharts({[metric.key]: avgValue}, metric.key, false, null, dataColorUtil)
        };
      })
    };
    
    let names = []
    // Third layer: Players
    positions.forEach((position) => {
      const playersData = data
        .filter(item => item.player_id__position === position)
        .map(player => {
          const playerName = `${player.player_id__first_name[0]}. ${player.player_id__last_name}`;
          names.push(playerName);
          
          return {
            name: playerName,
            y: Number(player[metric.key]?.toFixed(2)),
            acwr: Number(player[acwrKey]?.toFixed(2)),
            color: getBarColorsForHighcharts(player, metric.key, false, null, dataColorUtil),
          };
        });
    
      drilldowns[position] = {
        name: `Players`,
        id: position,
        data: playersData
      };
    });

    const posAvg = posSum / (positions.length > 0 ? positions.length : 100);
    
    return{ drilldowns, positions, names, posAvg};
  }

  function updateYAxisMax(chart, seriesData) {
    if(!seriesData) return
    if(Array.isArray(seriesData)){
      const maxValue = Math.max(...seriesData?.map(point => point?.y));
      const maxACWRValue = Math.max(...seriesData?.map(point => point?.acwr));
  
      if(maxValue){
        const newMax = maxValue * 1.1; 
        chart.yAxis[0].setExtremes(null, newMax);
      }

      if(maxACWRValue){
      const newMaxACWR = maxACWRValue * 1.1; 
      chart.yAxis[1].setExtremes(null, newMaxACWR);
      }

    } else {

      let maxPositionsACWRValue = undefined
      
      if(seriesData.yData && Array.isArray(seriesData.yData)) maxPositionsACWRValue = Math.max(...seriesData?.yData);
      
      if(maxPositionsACWRValue){
        const newMaxPositionsACWR = maxPositionsACWRValue * 1.1; 
        chart.yAxis[1].setExtremes(null, newMaxPositionsACWR);
      }
    }
  }

  if(!isForcePlateMetric) {
    const secondYAxis = {
      title: {
        text: ''
      },
      min: secondaryMax < 0 ? secondaryMax : 0,
      max: secondaryMax < 0 ? 0 : secondaryMax,
      opposite: true,
      gridLineWidth: 0,
      id: 'acwr',
      plotLines: []
    }

    options.yAxis.push(secondYAxis)
  }

  function getACWRPlotLines() {
    return [{
      id: 'acwr_upper',
      dashStyle: 'ShortDash',
      color: 'red',
      value: acwr_upper_limit,
      zIndex: 5
    },{
      id: 'acwr_lower',
      dashStyle: 'ShortDash',
      color: 'green',
      value: acwr_lower_limit,
      zIndex: 5
    }]
  }

  if (!showACWR && !isForcePlateMetric && !isTestingMetric && !isSubjectiveMetric) {
    const index = options.yAxis.findIndex((ax) => ax.id === 'acwr')
    if(index > -1) {
      const ACWRPlotlines = getACWRPlotLines()
      acwrPlot = [...ACWRPlotlines];
      options.yAxis[index].plotLines = [...options.yAxis[index].plotLines, ...ACWRPlotlines];
    }
  }

  if(showPredictions) {
    options = addPredictionSeriesConfig(options, metric.name, metric.key, predictionSeries, series, colors_100, dataColorUtil)
  }

  function getChartTeamAvgPlotLine({ avg, series }) {
    if (!avg) {
      avg = series.length ? (series.reduce((a, b) => a + b.y, 0) / series.length).toFixed(1) : 0;
    }
    const plotline = {
      id: 'avg',
      dashStyle: 'Dash',
      color: 'black',
      value: avg,
      zIndex: 5,
      label: {
        text: `Avg: ${avg}`,
        align: 'right'
      }
    }

    return plotline;
  }

  if (showAvgPlotline) {
    const plotline = getChartTeamAvgPlotLine({series});
    teamAvgPlot = [plotline];
    options.yAxis[0].plotLines.push(plotline);
  }

  return options;
};

export const mapBarChartData = ({seriesList = [], categories, title = "", colors, tooltip = null, avgPlotline = null, showHamburger = true}) => {
  // NOTE: series_list will be an array of arrays, with each array representing
  // a series of datapoints.
  if(!tooltip) {
    tooltip = { shared: true }
  }

  const options = {
    chart: {
      type: 'column',
      zoomType: 'xy'
    },
    title: {
      text: title
    },
    xAxis: {
      categories,
      gridLineWidth: 2,
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      tickWidth: 1
    },
    yAxis: [
      {
        title: {
          text: ''
        },
        tickAmount: 6,
        plotLines: []
      },
      {
        title: {
          text: ''
        },
        opposite: true,
        gridLineWidth: 0
      }
    ],
    legend: {
      enabled: true,
      verticalAlign: 'top',
      layout: 'horizontal'
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          y: categories.length > 20 ? 10 : 0,
          rotation: categories.length > 20 ? 270 : 0,
          inside: true
        }
      }
    },
    colors,
    series: seriesList,
    tooltip,
    exporting: {
      enabled: showHamburger
    }
  };

  if (avgPlotline) {
    const plotline = {
      dashStyle: 'Dash',
      color: 'black',
      value: avgPlotline,
      zIndex: 5,
      label: {
        text: `Avg: ${avgPlotline}`
      }
    }

    options.yAxis[0].plotLines.push(plotline);
  }

  return options;
};

export const mapDataForMaxVeloChart = ({
  dataIn,
  metric,
  teamName,
  showPredictions = false,
  teamColors,
  games = [],
  showRest = false,
  showTravel = false,
  restAndTravel = null,
  startDate = null,
  endDate = null,
  isTeamChart = false,
  ACWRLowerLimit = 0.8,
  ACWRUpperLimit = 1.3,
  showTitle = true,
  enableDrillDown = false,
  showHamburger = true,
  dataColorUtil = null,
  session = [], 
  playerGroup = [],
  position = [],
  selectedPlayerId = null
}) => {
  const acwr_upper_limit = ACWRUpperLimit;
  const acwr_lower_limit = ACWRLowerLimit;
  const categories = [];
  const colors_100 = dataIn?.colors;
  const key = metric?.key
  let data = dataIn?.data || []

  const chartData = {
    percSeriesData: [],
    effSeriesData: [],
    acwrSeriesData: [],
    predictionSeriesData: [],
    title: `${metric?.name} (${metric?.imperial_unit})`,
    metricName: metric?.name,
    ACWR: metric?.ACWR,
    imperialUnit: metric?.imperial_unit,
    metricUnit: metric?.metric_unit
  }

  if(showRest || showTravel) {
    const restAndTravelDates = getRestAndTravelDaysInChart(
      startDate,
      endDate,
      data,
      restAndTravel,
      showRest,
      showTravel
    );

    const maxValue = Math.max(
      ...(data.map((dp) => dp[key]).filter((x) => x) || [])
    );

    const maxPreValue = Math.max(
      ...(data.map((dp) => dp[key + "__pre"]).filter((x) => x) || [])
    );
  
    data = data.concat(
      restAndTravelDates.map((dateData) => ({
        ...dateData,
        disp_date: moment(dateData.date).format("MM-DD"),
        [key]: showPredictions
          ? showPredictions
            ? Math.max(maxValue, maxPreValue)
            : maxValue
          : maxValue,
      }))
    );
  }

  data.sort((a, b) => {
    if(isTeamChart) {
		  return a[key] > b[key] ? -1 : 1;
    } else {
      return a.date < b.date ? -1 : 1;
    }
	});

  data.forEach( (datapoint) => {
    if(isTeamChart) {
      categories.push(datapoint.name);
    } else {
      const opponent = games?.find((x) => x.date === datapoint.date)?.opponent || '';
      categories.push(opponent || moment(datapoint.date).format('MM-DD'));
    }
    const {
      percValue,
      effValue,
      acwrValue,
      effKey,
      predictionValue,
    } = maxVeloValues(datapoint, key)

    const restTravelLabel = getRestTravelLabel(datapoint)
    chartData.percSeriesData.push({
      y: percValue !== undefined ? percValue : null,
      color: getBarColorsForHighcharts(datapoint, key, null, null, dataColorUtil),
      restTravelLabel,
      ...(restTravelLabel
        ? {
            dataLabels: {
              format: '{point.restTravelLabel}',
              rotation: 270
            },
            opacity: 0.5,
         } : {})
    });
  
    chartData.effSeriesData.push({
      y: effValue !== undefined ? effValue : null,
      dataLabels: {
        enabled: true,
        style: {
          textOutline: 'none',
          fontWeight: 'normal',
          fontSize: '11px',
          stroke: getBarColorsForHighcharts(datapoint, effKey, null, null, dataColorUtil)
        },
        backgroundColor: 'white',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'black',
        color: getBarColorsForHighcharts(datapoint, effKey, null, null, dataColorUtil),
        padding: 5,
        shape: 'circle',
        verticalAlign: 'middle',
        align: 'center',
        y: 0,
        x: 0,
        useHTML: true,
        formatter: function() {
          return '<div style="width:20px;height:20px;line-height:20px;text-align:center;z-index:0;">' + this.y + '</div>';
        }
      }
    });

    chartData.predictionSeriesData.push(predictionValue !== undefined ? predictionValue : null)

    if(chartData.ACWR) {
      chartData.acwrSeriesData.push(acwrValue !== undefined ? acwrValue : null);
    }
  })

  const {percSeriesData, effSeriesData, acwrSeriesData, predictionSeriesData, title, ACWR, metricName} = chartData;
  const percSeries = {
    name: title,
    type: 'column',
    yAxis: 0,
    data: percSeriesData.map((item, index) => ({
      ...item,
      ...(enableDrillDown && {drilldown: `date-${index}`}),
      name: categories[index]
    })),
    dataLabels: {
      allowOverlap: true
    }
  }
  const effSeries = {
    name: `${metricName} Efforts`,
    type: 'spline',
    yAxis: 1,
    id: 'efforts',
    data: effSeriesData,
    color: 'rgb(70, 170, 197)',
    selected: false,
    connectNulls: true,
    dataLabels: {
      allowOverlap: true
    }
  }
  const acwrSeries = {
    name: 'ACWR',
    type: 'spline',
    yAxis: 1,
    data: acwrSeriesData,
    color: 'rgb(70, 170, 197)',
    marker: {
      enabled: true,
      radius: 3
    },
    selected: true,
    connectNulls: true
  }

  const allSeries = [percSeries, effSeries];
  if(ACWR) {
    allSeries.push(acwrSeries)
  }

  let positionsArray = [];
  let categoriesArray = [];
  let acwrPlot = [];
  let colors = null;
  let options = {
    chart: {
      type: 'column',
      zoomType: 'xy',
      animation: false,
      events: {
        load: function(e) {
          this.options.custom = this.options.custom || {};
          this.options.custom.title = showTitle ? title : '';
        },
        drilldown: function(e) {
          if (!e.seriesOptions) {
            const chart = this;
            const date = data[e.point.index]?.date;
            if (!chart.drilldownLevel) {
              chart.drilldownLevel = 0;
            }

            if (chart.drilldownLevel >= 2) {
              return; 
            }

            this.options.custom = this.options.custom || {};
            if (!chart?.drilldownData) {
              chart.showLoading('Loading data ...');
              categoriesArray = [...categories]; 
              
              trainingAPI.getDrillDownChartData({ 
                date: date,
                position,
                player_group: playerGroup,
                sessions: session,
                player_id: selectedPlayerId || null
              })
                .then(response => {
                  const drilldownData = response.player_data;
                  
                  colors = response.colors;
                  chart.drilldownData = drilldownData;
                  const {drilldowns, positions, posAvg} = createDrilldownStructure(drilldownData, metric, e.point.name, colors, enableDrillDown);

                  positionsArray = positions
                  if (ACWR) {
                    chart.yAxis[1].removePlotLine('acwr_upper');
                    chart.yAxis[1].removePlotLine('acwr_lower');
                  }


                  const xAxis = chart.xAxis[0];
                  xAxis.categories = positions
                  this.options.custom.currentCategories = positions;
                  this.options.custom.drilldown = "positional";
                  const series = drilldowns[e.point.name];
                  
                  chart.hideLoading();

                  const { effKey } = maxVeloValues(series, metric?.key)

                  chart.addSeriesAsDrilldown(e.point, series, effKey);
                  addSplineSeriesFromDrilldown(chart, series, effKey)

                  chart.applyDrilldown();
                  chart.yAxis[1].update({
                    visible: false
                  });
                  chart.drilldownLevel++;
                })
                .catch(error => {
                  console.error('Error fetching drill-down data:', error);
                  chart.hideLoading();
                });
            } else {
              
              const {drilldowns, names} = createDrilldownStructure(chart.drilldownData, metric, e.point.name, colors, enableDrillDown);

              const series = drilldowns[e.point.drilldown];

              if (ACWR && acwrPlot.length === 0) {
                acwrPlot = getACWRPlotLines();
              }
              if (ACWR) {
                chart.yAxis[1].removePlotLine('acwr_upper');
                chart.yAxis[1].removePlotLine('acwr_lower');
              }

              const xAxis = chart.xAxis[0];
              xAxis.categories = series?.data?.map(item => item.name);
              this.options.custom.currentCategories = xAxis.categories;
              this.options.custom.drilldown = "player";
              chart.addSeriesAsDrilldown(e.point, series);
              addSplineSeriesFromDrilldown(chart, series)

              chart.drilldownLevel++;
            }
          }
        },
        drillup: function() {
          const chart = this;
          chart.drilldownLevel--;
          this.options.custom = this.options.custom || {};
          if (chart.drilldownLevel === 1) {
            if (chart.drilldownData) delete chart.drilldownData;
            
            const xAxis = chart.xAxis[0];
            xAxis.categories = positionsArray
            this.options.custom.currentCategories = positionsArray;
            this.options.custom.drilldown = "positional";
            
            if (ACWR) {
              chart.yAxis[1].removePlotLine('acwr_upper');
              chart.yAxis[1].removePlotLine('acwr_lower');
            }

            let playerSeriesIndex = -1;
            chart.series.forEach((serie, index) => {
              if (serie.name === 'Players') {
                playerSeriesIndex = index;
              }
            });
            
            if (playerSeriesIndex !== -1) {
              chart.series[playerSeriesIndex].remove(false);
            } else {
              console.log('"Players" series not found');
            }
            
            chart.redraw();
          }
          if (chart.drilldownLevel === 0) {
            if (chart.drilldownData) delete chart.drilldownData;
            
            const xAxis = chart.xAxis[0];
            xAxis.categories = categories
            this.options.custom.currentCategories = categories;
            this.options.custom.drilldown = "";

            if (ACWR) {
              chart.yAxis[1].removePlotLine('acwr_upper');
              chart.yAxis[1].removePlotLine('acwr_lower');
              chart.yAxis[1].update({
                plotLines: acwrPlot
              });
            }

            let positionSeriesIndex = -1;
            chart.series.forEach((serie, index) => {
              if (serie.name === 'Positions') {
                positionSeriesIndex = index;
              }
            });
            
            if (positionSeriesIndex !== -1) {
              chart.series[positionSeriesIndex].remove(false);
            } else {
              console.log('"Positions" series not found');
            }
            
            chart.redraw();
          }

          chart.yAxis[1].update({
            visible: true
          });
        }
      }
    },
    drilldown: {
      activeAxisLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
        fontWeight: 500,
        tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
        tickWidth: 1
      },
      activeDataLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
      },
      series: []
    },
    title: {
      text: showTitle ? title : ''
    },
    xAxis: {
      categories,
      gridLineWidth: 2,
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      tickWidth: 1
    },
    yAxis: [
      {
        title: {
          text: ''
        },
        tickAmount: 6,
      },
      {
        title: {
          text: ''
        },
        min: 0,
        opposite: true,
        gridLineWidth: 0,
        plotLines: [],
        id: 'acwr'
      }
    ],
    legend: {
      enabled: true,
      verticalAlign: 'top',
      layout: 'horizontal'
    },
    tooltip: {
      shared: true,
      outside: true
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          inside: true,
          y: categories.length > 20 ? 10 : 0,
          rotation: categories.length > 20 ? 270 : 0,
          style: {
            textOutline: 'none',
          }
        }
      },
      spline: {
        dataLabels: {
          style: {
            textOutline: 'none'
          }
        }
      },
      series: {
        animation: false,
        events: {
          legendItemClick: function(event) {
            const series = this;
            if(!event?.target?.visible && event?.target?.userOptions?.id === 'efforts') {
              series.update({
                dataLabels: {
                  enabled: false
                }
              })
            }
          }
        },
      }
    },
    colors: teamColors,
    series: allSeries,
    exporting: {
      enabled: showHamburger,
      ...chartExportableOptions
    }
  };

  function getACWRPlotLines() {
    return [{
      id: 'acwr_upper',
      dashStyle: 'ShortDash',
      color: 'red',
      value: acwr_upper_limit,
      zIndex: 5
    },{
      id: 'acwr_lower',
      dashStyle: 'ShortDash',
      color: 'green',
      value: acwr_lower_limit,
      zIndex: 5
    }]
  }

  const ACWRPlotlines = getACWRPlotLines();

  const index = options.yAxis.findIndex((ax) => ax.id === 'acwr')
  if(ACWR && index) {
    acwrPlot = [...ACWRPlotlines];
    options.yAxis[index].plotLines = [...options.yAxis[index].plotLines, ...ACWRPlotlines];
  }

  if(showPredictions) {
    options = addPredictionSeriesConfig(options, metric.name, metric.key, predictionSeriesData, percSeriesData, colors_100, dataColorUtil)
  }

  function addSplineSeriesFromDrilldown(chart, series) {
    const data = series.data;

    const {
      effKey,
    } = maxVeloValues(data, key)

    const splineSeries = {
      type: 'spline',
      name: `${series.name}`,
      yAxis: 1,
      data: data.map(item => ({
        name: item.name,
        y: item.effValue
      })),
      color: 'rgb(70, 170, 197)',
      selected: false,
      connectNulls: true,
      dataLabels: {
        allowOverlap: true,
        enabled: true,
        style: {
          textOutline: 'none',
          fontWeight: 'normal',
          fontSize: '11px',
          stroke: getBarColorsForHighcharts(data, effKey, null, null, dataColorUtil)
        },
        backgroundColor: 'white',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'black',
        color: getBarColorsForHighcharts(data, effKey, null, null, dataColorUtil),
        padding: 5,
        shape: 'circle',
        verticalAlign: 'middle',
        align: 'center',
        y: 0,
        x: 0,
        useHTML: true,
        formatter: function() {
          return '<div style="width:20px;height:20px;line-height:20px;text-align:center;z-index:0;">' + this.y + '</div>';
        }
      }
    };
  
    chart.addSeries(splineSeries);
    chart.redraw();
  }

  return options;
}

function createDrilldownStructure(data, metric, date, colors = null, enableDrillDown = false) {
  const key = metric?.key
  const drilldowns = {};
  const positions = [...new Set(data.map(item => item.player_id__position))];
  
  const {
    effKey,
  } = maxVeloValues(data, key)

  drilldowns[date] = {
    name: 'Positions',
    id: date,
    data: positions.map((position, index) => {
      const positionData = data.filter(item => item.player_id__position === position);
      const avgValue = positionData.reduce((sum, item) => sum + item[metric.key], 0) / positionData.length;
      const avgEffValue = positionData.reduce((sum, item) => sum + (item[effKey] || 0), 0) / positionData.length;
      
      return {
        name: position,
        y: Number(avgValue?.toFixed(2)),
        effValue: Number(avgEffValue?.toFixed(2)),
        ...(enableDrillDown && {drilldown: position}),
        color: getBarColorsForHighcharts({[metric.key]: avgValue}, metric.key, false, null, dataColorUtil)
      };
    })
  };
  
  let names = []
  positions.forEach((position) => {
    const playersData = data
      .filter(item => item.player_id__position === position)
      .map(player => {
        const playerName = `${player.player_id__first_name[0]}. ${player.player_id__last_name}`;
        names.push(playerName);
        
        return {
          name: playerName,
          y: Number(player[metric.key]?.toFixed(2)),
          effValue: Number(player[effKey]?.toFixed(2)),
          color: getBarColorsForHighcharts(player, metric.key, false, null, dataColorUtil),
        };
      });
  
    drilldowns[position] = {
      name: `Players`,
      id: position,
      data: playersData
    };
  });

  return { drilldowns, positions, names};
}

function maxVeloValues (datapoint, key) {
  let percValue = null;
  let effValue = null;
  let acwrValue = null;
  let effKey = null;
  let predictionValue = datapoint[key + "_pre"]

  switch (key) {
    case "max_velo_50_69_perc":{
      percValue = datapoint["max_velo_50_69_perc"]
      effKey = "max_velo_50_69_effs"
      effValue = datapoint[effKey]
      break;
    }
    case "max_velo_70_79_perc": {
      percValue = datapoint["max_velo_70_79_perc"]
      effKey = "max_velo_70_79_effs"
      effValue = datapoint[effKey]
      break;
    }
    case "max_velo_80_85_perc": {
      percValue = datapoint["max_velo_80_85_perc"]
      effKey = "max_velo_80_85_effs"
      effValue = datapoint[effKey]
      break;
    }
    case "max_velo_gt_85_perc": {
      percValue = datapoint["max_velo_gt_85_perc"]
      effKey = "max_velo_gt_85_effs"
      effValue = datapoint[effKey]
      acwrValue = datapoint["max_velo_gt_85_perc_ACWR"]
      break;			
    }
    default: {}
  }

  return {percValue, effValue, acwrValue, effKey, predictionValue}
}

export const getBarColorForDrillData = (training, field, percentages, colors_100, team_name=undefined, dataColorUtil=undefined) => {
  let denom = 90;
  if (
    field === "top_speed" ||
    field.includes("per_min") ||
    field === "mean_hr" ||
    field === "max_hr"
  ) {
    denom = training["total_time"];
  }

  if (dataColorUtil) {
    const multiplier = (training["total_time"] || 0) / denom
    return dataColorUtil.get_gps_color(field, training[field], multiplier)
  }

	return "#000000";
}

export const getCustomTooltip = (isForcePlateMetric, isSubjectiveMetric, isRPEChart, isTestingMetric, tooltip) => {
  if(isForcePlateMetric) {
    return {
      ...tooltip,
      formatter: function () {
        return `<strong>${this?.x}</strong> <br> Avg: ${this?.point?.y} <br> Max: ${this?.point?.max} <br> Min: ${this?.point?.min} <br> Diff: ${this?.point?.diff}`
      }
    }
  }
  if (isTestingMetric) {
    return {
      ...tooltip,
      formatter: function () {
        return `<p>${JSON.stringify(this?.point)} ${this?.x} ${this?.y}</p>`
      }
    }
  }

  if(isSubjectiveMetric) {
    if(isRPEChart) {
      return {
        ...tooltip,
        formatter: function () {
          return `
          ${this?.x} <br/> 
          ${this?.point?.short}: <b>${this?.point?.y}</b>
          `
        }
      }
    } else {
      return {
        ...tooltip,
        formatter: function () {
          return `
          ${this?.x} <br/> 
          ${this?.point?.short}: <b>${this?.point?.text}</b>
          `
        }
      }
    }
  }

  return tooltip
}

export const mapDataForOverviewV2Chart = (chartData) => {

  if(!chartData.data || !chartData.metrics) {
    return
  }

  const rpe_acute = "#800000";
  const rpe_chronic = "#00008B";
  const bar_color = "#31b452"

  const categories = chartData.data.map( (item) => item.opponent ? item.opponent : item.disp_date)

  const seriesList = chartData.metrics.map( (metric) => {
    const series_color = metric.type === 'bar' ? bar_color : metric.key == 'RPE_acute' ? rpe_acute : rpe_chronic;
    return {
      name: metric.label,
      type: metric.type === 'bar' ? 'column' : metric.type,
      yAxis: metric.type === 'bar' ? 0 : 1,
      color: series_color,
      connectNulls: true,
      data: chartData.data.map( (item) => {
        return {
          y: metric?.key ? item[metric?.key] || 0 : 0,
          color: metric.type === 'bar' ? item['bar_hex'] : null,
        }
      }),
    }
  })

  const options = {
    chart: {
      type: 'column',
      zoomType: 'xy'
    },
    drilldown: {
      series: []
    },
    title: {
      text: ''
    },
    xAxis: {
      categories,
      gridLineWidth: 2,
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      tickWidth: 1
    },
    yAxis: [
      {
        title: {
          text: ''
        },
        tickAmount: 6,
        plotLines: []
      },
      {
        title: {
          text: ''
        },
        opposite: true,
        gridLineWidth: 0
      }
    ],
    legend: {
      enabled: true,
      verticalAlign: 'top',
      layout: 'horizontal'
    },
    tooltip: {
      shared: true
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          y: categories.length > 20 ? 10 : 0,
          rotation: categories.length > 20 ? 270 : 0,
          inside: true
        }
      }
    },
    series: seriesList,
  };

  return options;
};